/* TDB Bundle BOM · Sprint E.5 UI
 *
 * Read-mostly screen showing every hamper/combo bundle with its live
 * computed food cost from constituent components. Roji can edit any
 * bundle's composition (food_component_1, food_component_2,
 * non_food_component) inline · cost recomputes live.
 *
 * Components resolve via the same engine as the backend service:
 *   variant_title match → product_title match (averaged) → non-food
 *   master → unresolved (flagged red).
 *
 * Filter to "Needs Roji" surfaces bundles with empty or unresolved
 * components first · that's Roji's day-1 worklist.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Package, Loader2, Search, AlertTriangle, CheckCircle2, X,
  Edit3, Sparkles, HelpCircle,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtRupees = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function TDBBundleBOM({ currentUser }) {
  const [bundles, setBundles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('needs_roji');
  const [editing, setEditing] = useState(null);

  const email = (currentUser?.email || '').toLowerCase();
  const isCEO = currentUser?.role === 'ceo' || email === 'dipali@clubconcierge.in';
  const canEdit = isCEO || email === 'roji.mathew@mindescapes.in' || email === 'sikand.adi@gmail.com';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tdb-mis/bundles');
      setBundles(r.data?.bundles || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return bundles.filter((b) => {
      if (filter === 'needs_roji' && !b.is_missing_data) return false;
      if (filter === 'complete' && b.is_missing_data) return false;
      if (filter === 'negative_margin' && b.margin_pct >= 0) return false;
      if (q) {
        const hay = `${b.bundle_product} ${b.bundle_variant}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [bundles, filter, search]);

  if (loading) {
    return (
      <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading bundles…
      </div>
    );
  }

  return (
    <div data-testid="tdb-bundle-bom" className="space-y-4">
      <div className="bg-white border rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-5 h-5 text-violet-700" />
              <h2 className="text-base font-semibold text-slate-900">
                Bundle BOM · Hampers & Combos
              </h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium uppercase tracking-wider">
                Sprint E.5
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Live cost computed from components · rates flow from the Ingredient Master ·
              {' '}every food + non-food rate change cascades into bundle costs automatically.
            </p>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <Stat label="Total" value={summary.total} />
            <Stat label="Complete" value={summary.complete} accent="emerald" />
            <Stat label="Needs Roji" value={summary.missing} accent="amber" />
            <Stat label="Unresolved comp." value={summary.unresolved} accent="red" />
            <Stat label="Avg margin" value={`${summary.avg_margin_pct}%`} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white border rounded-2xl p-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <input
            data-testid="tdb-bb-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bundle name…"
            className="w-full text-sm pl-9 pr-3 py-2 rounded-md border border-slate-200"
          />
        </div>
        <select
          data-testid="tdb-bb-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm px-2 py-2 rounded-md border border-slate-200"
        >
          <option value="needs_roji">Needs Roji (incomplete)</option>
          <option value="complete">Complete only</option>
          <option value="negative_margin">Negative margin</option>
          <option value="all">All bundles</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">
          {filtered.length} shown · {bundles.length} total
        </span>
      </div>

      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Bundle</th>
              <th className="text-left px-3 py-2">Components</th>
              <th className="text-right px-3 py-2">Food ₹</th>
              <th className="text-right px-3 py-2">NF ₹</th>
              <th className="text-right px-3 py-2">Total ₹</th>
              <th className="text-right px-3 py-2">SP ₹</th>
              <th className="text-right px-3 py-2">Margin</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map((b, i) => (
              <BundleRow
                key={`${b.bundle_product}|${b.bundle_variant}|${i}`}
                bundle={b}
                canEdit={canEdit}
                onEdit={() => setEditing(b)}
              />
            ))}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div className="text-center py-2 text-xs text-slate-500 border-t bg-slate-50">
            …and {filtered.length - 500} more bundles (refine search to narrow)
          </div>
        )}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500">
            No bundles match.
          </div>
        )}
      </div>

      {editing && (
        <BundleEditModal
          bundle={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function Stat({ label, value, accent = 'slate' }) {
  const palette = {
    slate: 'bg-slate-50 text-slate-900 border-slate-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-300',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-300',
    red: 'bg-red-50 text-red-900 border-red-300',
  }[accent];
  return (
    <div className={`px-3 py-2 rounded-md border ${palette}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}


function BundleRow({ bundle, canEdit, onEdit }) {
  const slug = (s) => (s || '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const testid = `tdb-bb-row-${slug(bundle.bundle_product)}-${slug(bundle.bundle_variant)}`;
  return (
    <tr data-testid={testid} className={`border-t border-slate-100 ${bundle.is_missing_data ? 'bg-amber-50/30' : ''}`}>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-900 text-sm">{bundle.bundle_product}</div>
        <div className="text-xs text-slate-500">{bundle.bundle_variant}</div>
      </td>
      <td className="px-3 py-2">
        <div className="space-y-0.5">
          {bundle.components.map((c, i) => (
            <ComponentChip key={i} c={c} />
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono text-sm">{fmtRupees(bundle.food_cost)}</td>
      <td className="px-3 py-2 text-right font-mono text-sm">{fmtRupees(bundle.non_food_cost)}</td>
      <td className="px-3 py-2 text-right font-mono text-sm font-semibold">{fmtRupees(bundle.total_cost)}</td>
      <td className="px-3 py-2 text-right font-mono text-sm">{fmtRupees(bundle.selling_price)}</td>
      <td className="px-3 py-2 text-right">
        <span className={`text-xs font-medium ${
          bundle.margin_pct < 0 ? 'text-red-700'
            : bundle.margin_pct < 50 ? 'text-amber-700'
            : 'text-emerald-700'
        }`}>
          {bundle.margin_pct.toFixed(1)}%
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {canEdit && (
          <button
            data-testid={`tdb-bb-edit-${slug(bundle.bundle_product)}-${slug(bundle.bundle_variant)}`}
            onClick={onEdit}
            className="text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
        )}
      </td>
    </tr>
  );
}


function ComponentChip({ c }) {
  if (!c.kind) {
    return (
      <div className="text-[11px] text-slate-400 italic">
        {c.field.replace('_', ' ')}: (empty)
      </div>
    );
  }
  const palette = {
    sku_variant: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    sku_product: c.ambiguous
      ? 'bg-amber-100 text-amber-900 border-amber-200'
      : 'bg-blue-100 text-blue-900 border-blue-200',
    non_food: 'bg-violet-100 text-violet-900 border-violet-200',
    unresolved: 'bg-red-100 text-red-900 border-red-200',
  }[c.kind];
  const icon = c.kind === 'unresolved' ? '❓'
    : c.kind === 'non_food' ? '🎁'
    : c.ambiguous ? '~' : '✓';
  return (
    <div className={`text-[11px] px-1.5 py-0.5 rounded border ${palette} inline-flex items-center gap-1 mr-1`}>
      <span className="font-mono">{icon}</span>
      <span className="truncate max-w-[280px]">{c.name}</span>
      {c.cost > 0 && <span className="font-mono opacity-70">· {fmtRupees(c.cost)}</span>}
      {c.ambiguous && <span className="opacity-60 text-[10px]">(avg of {c.variant_count})</span>}
    </div>
  );
}


function BundleEditModal({ bundle, onClose, onSaved }) {
  const [f1, setF1] = useState(bundle.components.find((c) => c.field === 'food_component_1')?.name || '');
  const [f2, setF2] = useState(bundle.components.find((c) => c.field === 'food_component_2')?.name || '');
  const [nf, setNF] = useState(bundle.components.find((c) => c.field === 'non_food_component')?.name || '');
  const [sp, setSP] = useState(String(bundle.selling_price));
  const [notes, setNotes] = useState(bundle.notes || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Note: server-side resolver uses these names against SKUs+non-food master.
  // Type the full Shopify variant_title (e.g. "Oats / Chicken") for best match.

  const save = async () => {
    if (!reason.trim()) {
      toast.error('Reason mandatory · what did you change and why');
      return;
    }
    setSubmitting(true);
    try {
      await api.put(
        `/tdb-mis/bundles/${encodeURIComponent(bundle.bundle_product)}/${encodeURIComponent(bundle.bundle_variant)}`,
        {
          food_component_1: f1,
          food_component_2: f2,
          non_food_component: nf,
          selling_price: parseFloat(sp) || 0,
          notes,
          reason: reason.trim(),
        },
      );
      toast.success('Bundle saved · cost recomputed');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="tdb-bb-edit-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{bundle.bundle_product}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{bundle.bundle_variant}</p>
          </div>
          <button data-testid="tdb-bb-modal-close" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 flex items-start gap-2">
            <HelpCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Type the <strong>Shopify variant title</strong> (e.g. <code className="font-mono bg-white px-1 rounded">Oats / Chicken</code>) for best match.
              {' '}For non-food, use the master name (e.g. <code className="font-mono bg-white px-1 rounded">Bandana</code>).
              {' '}Leaving a field blank is OK — the bundle just gets a partial cost.
            </span>
          </div>

          <FieldRow label="Food component 1"
                    testid="tdb-bb-edit-food1"
                    value={f1} onChange={setF1} />
          <FieldRow label="Food component 2"
                    testid="tdb-bb-edit-food2"
                    value={f2} onChange={setF2} />
          <FieldRow label="Non-food component"
                    testid="tdb-bb-edit-nonfood"
                    value={nf} onChange={setNF}
                    hint="e.g. Bandana · Feeding Mat · Toy" />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-700">Selling price ₹</label>
              <input
                data-testid="tdb-bb-edit-sp"
                type="number" step="0.01"
                value={sp}
                onChange={(e) => setSP(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
              <input
                data-testid="tdb-bb-edit-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">
              Reason <span className="text-red-600">*</span>
            </label>
            <input
              data-testid="tdb-bb-edit-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. fixed pack composition after Shopify variant rename"
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-300">Cancel</button>
          <button
            data-testid="tdb-bb-edit-save"
            onClick={save}
            disabled={submitting || !reason.trim()}
            className="text-sm px-4 py-2 rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Save & recompute
          </button>
        </div>
      </div>
    </div>
  );
}


function FieldRow({ label, testid, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={200}
        className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 font-mono"
      />
      {hint && (
        <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
}
